import { ID } from '../core/common';
import {
  Event,
  EventRecord,
  ListEventOptions,
  ListEventsResponseEntry,
  NotSentRemindersCountDto,
  SyncJobStatus,
  UpdateEventBodyDto,
  UpdateEventResponseDto,
  UserEventStatsDto,
} from '../core/events';
import type { HttpClient } from './http';
import { catch404ReturnNull } from './util';

export type UpdateExtendEventBodyDto = UpdateEventBodyDto & {
  id: ID; // Our database ID
  event_id: ID; // Google Calendar event ID
};

/**
 * Return value of `getEventList`: the entries array with the optional
 * truncation metadata attached as non-index properties. The HTTP client
 * unwraps the `{ data, truncated, ... }` envelope into the inner entries array
 * and carries `truncated`/`total_in_range`/`returned_count` alongside it, so
 * callers treat the value as a normal array while the dashboard can still read
 * `truncated` to warn when the server capped a large date range or a dense
 * account.
 */
export type EventListResult = ListEventsResponseEntry[] & {
  truncated?: boolean;
  total_in_range?: number;
  returned_count?: number;
};

export interface EventsService {
  ensureEventExists: (
    googleCalendarId: string,
    googleEventId: string,
  ) => Promise<{ id: string }>;
  getEventWithReminders: (id: string) => Promise<Event | null>;
  getEventList: (params?: ListEventOptions) => Promise<EventListResult>;
  getUserEventStats: () => Promise<UserEventStatsDto>;
  getNotSentRemindersCount: () => Promise<NotSentRemindersCountDto>;
  getSyncJobStatus: (jobId: string) => Promise<SyncJobStatus>;
  syncEvents: () => Promise<void>;
  updateEvent: (
    dto: Partial<UpdateExtendEventBodyDto>,
  ) => Promise<EventRecord & UpdateEventResponseDto>;
}

export function createEventsService(
  httpClient: HttpClient,
  baseUrl: string,
): EventsService {
  const BASE_URL = `${baseUrl}/events`;

  return {
    ensureEventExists: (googleCalendarId: string, googleEventId: string) => {
      return httpClient<{ id: string }>(
        `${BASE_URL}/sync/${googleCalendarId}/${googleEventId}`,
        'post',
      );
    },

    getEventWithReminders: (id: string): Promise<Event | null> => {
      return catch404ReturnNull(() =>
        httpClient<Event>(`${BASE_URL}/${id}`, 'get'),
      );
    },

    getEventList: (params?: ListEventOptions): Promise<EventListResult> => {
      // The raw HTTP client already unwraps the `{ data, truncated, ... }`
      // envelope into the inner entries array and attaches the truncation
      // metadata (`truncated`/`total_in_range`/`returned_count`) as non-index
      // properties on that array. Return it directly so the React Query cache
      // stays array-shaped for optimistic updaters that write the raw cache.
      return httpClient<EventListResult>(`${BASE_URL}`, 'get', {
        query: params as Record<string, unknown>,
      });
    },

    getUserEventStats: () => {
      return httpClient<UserEventStatsDto>(`${BASE_URL}/stats`, 'get');
    },

    getNotSentRemindersCount: () => {
      return httpClient<NotSentRemindersCountDto>(
        `${BASE_URL}/not-sent-count`,
        'get',
      );
    },

    getSyncJobStatus: (jobId: string) => {
      return httpClient<SyncJobStatus>(`${BASE_URL}/sync/${jobId}`, 'get');
    },

    syncEvents: () => {
      return httpClient<void>(`${BASE_URL}/sync`, 'post');
    },

    updateEvent: (event: Partial<UpdateExtendEventBodyDto>) => {
      const offsetProps = [
        'confirmation_offsets',
        'reminder_offsets',
        'follow_up_offsets',
      ];
      offsetProps.forEach((prop) => {
        const key = prop as keyof Pick<
          UpdateExtendEventBodyDto,
          'confirmation_offsets' | 'reminder_offsets' | 'follow_up_offsets'
        >;
        if (event[key]?.length) {
          event[key] = event[key].map((offset: number) => +offset);
        }
      });
      return httpClient<EventRecord & UpdateEventResponseDto>(
        `${BASE_URL}`,
        'patch',
        {
          body: event,
        },
      );
    },
  };
}
